import { AttachMoney, Edit, Shield } from "@mui/icons-material";
import {
  ApiIncrementalityControlGroupType,
  ApiMetric,
} from "@incendium/api";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import GlassCard from "Components/GlassCard/GlassCard";
import moment from "moment";
import { METRIC_ICON_MAP, METRIC_LABEL_MAP } from "../helpers/metricOptions";
import {
  buildReviewHoldoutNotice,
  CONTROL_GROUP_LABEL,
  CONTROL_GROUP_QUALITY_LABEL,
  CONTROL_PERIOD_LABEL,
  formatReviewDateRange,
  GEO_SWITCH_OFF_LABEL,
  GEO_SWITCH_OFF_REVIEW_DESCRIPTION,
  getControlGroupLabel,
  OVERALL_SIMILARITY_SCORE_PREFIX,
  REQUIRED_BEFORE_LAUNCH_LABEL,
  REVIEW_READY_SUBTITLE,
  REVIEW_READY_TITLE,
  TEST_NAME_LABEL,
  TEST_PERIOD_LABEL,
  TEST_REGION_LABEL,
  WHY_THIS_TEST_DESCRIPTION,
  WHY_THIS_TEST_LABEL,
} from "../helpers/copy";
import { useIncrementalitySetupContext } from "../providers/IncrementalitySetupProvider";
import { MetricIconBubble, SectionSubTitle, SectionTitle } from "./styled";

type IncrementalityReviewTabProps = {
  onEditTest: () => void;
  onEditMetrics: () => void;
};

function IncrementalityReviewTab({
  onEditTest,
  onEditMetrics,
}: IncrementalityReviewTabProps) {
  const { incrementalityTest, trendScore } = useIncrementalitySetupContext();
  const isAutoMatched =
    incrementalityTest.controlGroupType ===
    ApiIncrementalityControlGroupType.INCREMENTALITY_CONTROL_GROUP_TYPE_AUTO_MATCHED;
  const primaryMetricId = incrementalityTest.primaryMetric as ApiMetric;
  const supportingMetricIds =
    (incrementalityTest.supportingMetrics as ApiMetric[]) || [];

  const controlGroupLabel = getControlGroupLabel(
    incrementalityTest.controlGroupType,
    {
      testRegionCity: incrementalityTest.testRegionCity,
      testRegionCountry: incrementalityTest.testRegionCountry,
    },
  );
  const reviewHoldoutNotice = buildReviewHoldoutNotice(incrementalityTest);

  return (
    <Stack spacing={2.5}>
      <GlassCard boxProps={{ p: 2 }} noShadow>
        <Stack direction="row" spacing={1} alignItems="center">
          <Shield color="success" fontSize="small" />
          <Box>
            <Typography fontWeight={700}>{REVIEW_READY_TITLE}</Typography>
            <Typography variant="body2" color="text.secondary">
              {REVIEW_READY_SUBTITLE}
            </Typography>
          </Box>
        </Stack>
      </GlassCard>

      <GlassCard boxProps={{ p: 2 }} noShadow>
        <Stack spacing={2}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <SectionTitle>Test</SectionTitle>
            <Button
              size="small"
              startIcon={<Edit fontSize="small" />}
              onClick={onEditTest}
            >
              Edit
            </Button>
          </Box>

          <Box>
            <SectionSubTitle>{TEST_NAME_LABEL}</SectionSubTitle>
            <Typography fontWeight={700}>
              {incrementalityTest.name || "-"}
            </Typography>
          </Box>

          <Divider />

          <Box>
            <SectionSubTitle>1. Test type & action</SectionSubTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography fontWeight={700}>{GEO_SWITCH_OFF_LABEL}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {GEO_SWITCH_OFF_REVIEW_DESCRIPTION}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  {WHY_THIS_TEST_LABEL}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {WHY_THIS_TEST_DESCRIPTION}
                </Typography>
              </Grid>
            </Grid>
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              <Typography variant="body2" fontWeight={700}>
                {REQUIRED_BEFORE_LAUNCH_LABEL}
              </Typography>
              <Typography variant="body2">{reviewHoldoutNotice}</Typography>
            </Alert>
          </Box>

          <Divider />

          <Box>
            <SectionSubTitle>2. Dates</SectionSubTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  {TEST_PERIOD_LABEL}
                </Typography>
                <Typography variant="body2">
                  {formatReviewDateRange(
                    incrementalityTest.testFrom
                      ? moment(incrementalityTest.testFrom)
                      : null,
                    incrementalityTest.testTo
                      ? moment(incrementalityTest.testTo)
                      : null,
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  {CONTROL_PERIOD_LABEL}
                </Typography>
                <Typography variant="body2">
                  {formatReviewDateRange(
                    incrementalityTest.preTestFrom
                      ? moment(incrementalityTest.preTestFrom)
                      : null,
                    incrementalityTest.preTestTo
                      ? moment(incrementalityTest.preTestTo)
                      : null,
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <SectionSubTitle>3. Region & control</SectionSubTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  {TEST_REGION_LABEL}
                </Typography>
                <Typography fontWeight={700}>
                  {[
                    incrementalityTest.testRegionCity,
                    incrementalityTest.testRegionCountry,
                  ]
                    .filter(Boolean)
                    .join(", ") || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  {CONTROL_GROUP_LABEL}
                </Typography>
                <Typography fontWeight={700}>{controlGroupLabel}</Typography>
              </Grid>
            </Grid>
            {isAutoMatched && (
              <Box
                sx={{
                  mt: 1.5,
                  border: (theme) => `1px solid ${theme.palette.success.light}`,
                  background: (theme) => `${theme.palette.success.light}20`,
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <SectionSubTitle>
                      {CONTROL_GROUP_QUALITY_LABEL}
                    </SectionSubTitle>
                    <Typography variant="body2" color="text.secondary">
                      {OVERALL_SIMILARITY_SCORE_PREFIX}
                      <Typography
                        component="span"
                        fontWeight={700}
                        sx={{ ml: 0.5 }}
                      >
                        {trendScore != null ? `${Math.round(trendScore)}%` : "-"}
                      </Typography>
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}
          </Box>
        </Stack>
      </GlassCard>

      <GlassCard boxProps={{ p: 2 }} noShadow>
        <Stack>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <SectionTitle>Metrics</SectionTitle>
            <Button
              size="small"
              startIcon={<Edit fontSize="small" />}
              onClick={onEditMetrics}
            >
              Edit
            </Button>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <SectionSubTitle mb={1}>3. Primary metric</SectionSubTitle>
              <Stack direction="row" spacing={1} alignItems="center">
                <MetricIconBubble>
                  {METRIC_ICON_MAP[primaryMetricId] || (
                    <AttachMoney fontSize="small" />
                  )}
                </MetricIconBubble>
                <Typography variant="body2">
                  {METRIC_LABEL_MAP[primaryMetricId] || "Revenue per session"}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <SectionSubTitle mb={2}>
                4. Supporting metrics ({supportingMetricIds.length})
              </SectionSubTitle>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {supportingMetricIds.map((metricId) => (
                  <Chip
                    key={metricId}
                    size="small"
                    label={METRIC_LABEL_MAP[metricId] || metricId}
                  />
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </GlassCard>
    </Stack>
  );
}

export default IncrementalityReviewTab;
